import React, { useState, useEffect } from 'react';
import { Card, CardBody, Col, Container, Row, Button, Form, FormGroup, Label, Input, Spinner, FormFeedback } from 'reactstrap';
import { useForm, Controller } from 'react-hook-form';
import { Edit, Trash2, RotateCcw } from 'react-feather';
import DataTable from 'react-data-table-component';
import { apiPost } from '../../Utils';
import Breadcrumbs from '../../CommonElements/Breadcrumbs';
import { ToastContainer, toast } from 'react-toastify';
import Swal from 'sweetalert2';
import "react-toastify/dist/ReactToastify.css";
import { H3 } from '../../AbstractElements';
import { useAuth } from '../../Context/AuthContext';

interface MasterEntryProps {
    type: string;
    title: string;
}

const MasterEntryPage: React.FC<MasterEntryProps> = ({ type, title }) => {
    const [data, setData] = useState<any[]>([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [loading, setLoading] = useState(false);
    const [formLoading, setFormLoading] = useState(false);
    const [editId, setEditId] = useState<number | null>(null);
    const { hasPermission } = useAuth();

    const moduleSlug = type.replace(/_/g, '-');
    const canView = hasPermission(`${moduleSlug}.view`);
    const canAdd = hasPermission(`${moduleSlug}.add`);
    const canEdit = hasPermission(`${moduleSlug}.edit`);
    const canDelete = hasPermission(`${moduleSlug}.delete`);

    const { control, handleSubmit, reset, setValue, formState: { errors } } = useForm({
        defaultValues: { name: '' }
    });

    const customStyles = {
        headRow: {
            style: {
                backgroundColor: '#e0f2f1',
                color: '#00695c',
                minHeight: '50px',
                borderBottom: '1px solid #e0e0e0',
                fontWeight: 'bold',
                fontSize: '14px',
            },
        },
        headCells: {
            style: {
                fontSize: '15px',
                fontWeight: '600',
                paddingLeft: '16px',
                paddingRight: '16px',
            },
        },
        rows: {
            style: {
                minHeight: '50px',
                '&:not(:last-of-type)': {
                    borderBottomStyle: 'solid',
                    borderBottomWidth: '1px',
                    borderBottomColor: '#e0e0e0',
                },
            },
        },
    };

    const columns = [
        {
            name: 'Sl.No',
            selector: (row: any, index?: number) => (index ?? 0) + 1,
            sortable: true,
            width: '150px'
        },
        {
            name: `${title}`,
            selector: (row: any) => row.name,
            sortable: true,
        },
        {
            name: 'Actions',
            cell: (row: any) => (
                <div className="d-flex gap-2">
                    {canEdit && (
                        <Button color="primary" size="sm" onClick={() => handleEdit(row)}>
                            <Edit size={14} />
                        </Button>
                    )}
                    {canDelete && (
                        <Button color="danger" size="sm" onClick={() => handleDelete(row.id)}>
                            <Trash2 size={14} />
                        </Button>
                    )}
                </div>
            ),
            center: true,
        }
    ];

    const fetchData = async () => {
        setLoading(true);
        try {
            const res: any = await apiPost('master-entries/list', { type });
            setData(Array.isArray(res) ? res : []);
        } catch (error) {
            toast.error('Failed to fetch data');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (canView) {
            fetchData();
        }
        resetForm();
    }, [type, canView]);

    const resetForm = () => {
        reset({ name: '' });
        setEditId(null);
    };

    const onSubmit = async (formData: { name: string }) => {
        setFormLoading(true);
        try {
            if (editId) {
                await apiPost(`master-entries/update/${editId}`, { name: formData.name });
                toast.success('Record updated successfully');
            } else {
                await apiPost('master-entries/store', { type, name: formData.name });
                toast.success('Record added successfully');
            }
            resetForm();
            fetchData();
        } catch (error) {
            toast.error('Operation failed');
        } finally {
            setFormLoading(false);
        }
    };

    const handleEdit = (item: any) => {
        setValue('name', item.name);
        setEditId(item.id);
    };

    const handleDelete = async (id: number) => {
        const result = await Swal.fire({
            title: 'Are you sure?',
            text: "This record will be soft deleted!",
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#3085d6',
            cancelButtonColor: '#d33',
            confirmButtonText: 'Yes, delete it!'
        });

        if (result.isConfirmed) {
            try {
                await apiPost(`master-entries/delete/${id}`, {});
                toast.success('Record deleted');
                fetchData();
            } catch (error) {
                toast.error('Failed to delete');
            }
        }
    };

    const filteredData = data.filter(item =>
        item.name.toLowerCase().includes(searchTerm.toLowerCase())
    );

    if (!canView) {
        return (
            <div className="page-body">
                <Container fluid>
                    <Card>
                        <CardBody className="text-center py-5">
                            <H3 className="text-danger">Access Denied</H3>
                            <p>You do not have permission to view this page.</p>
                        </CardBody>
                    </Card>
                </Container>
            </div>
        );
    }

    return (
        <div className="page-body">
            <Breadcrumbs parent="Master Entry" title={title} pageTitle={title} />
            <Container fluid>
                <Row className="g-4">
                    {/* Add/Edit Form Section */}
                    {(canAdd || (editId && canEdit)) && (
                        <Col xl="4">
                            <Card className="border-0 shadow-sm rounded-4">
                                <CardBody className="p-4">
                                    <H3 className="text-primary mb-4">{editId ? 'Edit' : 'Add'} {title}</H3>
                                    <Form onSubmit={handleSubmit(onSubmit)}>
                                        <FormGroup className="mb-4">
                                            <Label className="fw-bold mb-2 text-dark" style={{ fontSize: '13px' }}>{title} <span className="text-danger">*</span></Label>
                                            <Controller
                                                name="name"
                                                control={control}
                                                rules={{ required: `${title} is required` }}
                                                render={({ field }) => (
                                                    <>
                                                        <Input
                                                            {...field}
                                                            type="text"
                                                            placeholder={`Enter ${title}`}
                                                            className="form-control shadow-none"
                                                            style={{ fontSize: '13px' }}
                                                            invalid={!!errors.name}
                                                            disabled={formLoading}
                                                        />
                                                        <FormFeedback>{errors.name?.message}</FormFeedback>
                                                    </>
                                                )}
                                            />
                                        </FormGroup>
                                        <div className="d-flex gap-2">
                                            <Button
                                                color="primary"
                                                type="submit"
                                                className="px-3 py-2 text-white border-0 transition-all rounded-2 shadow-sm"
                                                style={{ backgroundColor: '#2d9a91', minWidth: '80px', fontSize: '13px' }}
                                                disabled={formLoading}
                                            >
                                                {formLoading ? <Spinner size="sm" /> : (editId ? 'Update' : 'Submit')}
                                            </Button>
                                            {editId && (
                                                <Button
                                                    color="light"
                                                    type="button"
                                                    onClick={resetForm}
                                                    className="px-3"
                                                    disabled={formLoading}
                                                >
                                                    <RotateCcw size={16} />
                                                </Button>
                                            )}
                                        </div>
                                    </Form>
                                </CardBody>
                            </Card>
                        </Col>
                    )}

                    {/* Table Section */}
                    <Col xl={(canAdd || (editId && canEdit)) ? "8" : "12"}>
                        <Card className="border-0 shadow-sm rounded-4">
                            <CardBody className="p-4">
                                <div className="d-flex justify-content-between align-items-center mb-4">
                                    <H3 className="text-primary mb-0">{title} Overview</H3>
                                    <div className="d-flex align-items-center gap-2">
                                        <Input
                                            type="text"
                                            className="form-control-sm border shadow-none"
                                            style={{ width: '200px', borderColor: '#e0e0e0' }}
                                            placeholder="Search ..."
                                            value={searchTerm}
                                            onChange={(e) => setSearchTerm(e.target.value)}
                                        />
                                    </div>
                                </div>

                                <div className="table-responsive rounded-3 border">
                                    <DataTable
                                        columns={columns}
                                        data={filteredData}
                                        pagination
                                        highlightOnHover
                                        progressPending={loading}
                                        progressComponent={<Spinner color="primary" className="my-5" />}
                                        customStyles={customStyles}
                                    />
                                </div>
                            </CardBody>
                        </Card>
                    </Col>
                </Row>
            </Container>
            <ToastContainer autoClose={1500} />
        </div>
    );
};

export default MasterEntryPage;